import {
    ArrayExpr,
    BinaryExpr,
    CellReference,
    ComparisonExpr,
    ConcatExpr,
    FormulaExpression,
    FormulaValueExpression,
    FrameCalculatedColumnExpr,
    FrameColumnReference,
    IdentifierLiteral,
    IntersectionReference,
    InvokeFunctionExpr,
    LazyFormulaExpression,
    ListReference,
    LiveFormulaExpression,
    NegateExpr,
    ParenExpr,
    PercentExpr,
    RangeReference,
    UnionReference,
    ValueLiteral
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaASTNodes';
import {FunctionNameLiteral, IFormulaNode} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaDefinitions';
import {
    BaseFormulaVisitor,
    IFormulaVisitorAsync,
    ShouldContinueTraversal
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaVisitor';
import {EvalFormulaResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/EvalFormulaResult';
import {FormulaEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaEngine';
import {FormulaValueCoercionUtils} from '@mintdata-wsp/common/controllers/spreadsheet/formula/FormulaValueCoercionUtils';
import {EmptySheetRange} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/EmptySheetRange';
import {SheetAPIImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetAPIImpl';
import {FormulaExpressionImpl, SheetContextImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetContextImpl';
import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {SheetRangeImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetRangeImpl';
import {SheetRangeListImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetRangeListImpl';
import {TimerContextImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/TimerContextImpl';
import {FunctionUtil} from '@mintdata-wsp/common/controllers/spreadsheet/formula/invoke/FunctionUtil';
import {ArrayFormulaEvalLogic} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/ArrayFormulaEvalLogic';
import {ICellIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/CellIOContext';
import {EvalLogic} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/EvalLogic';
import {EvalStack} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/EvalStack';
import {IFunctionResolver} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/FunctionResolver';
import {ISheetIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/SheetIOContext';
import {ClientFunction, FunctionArg} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {
    INamingContext,
    ISheetAPI,
    ISheetRange,
    ISheetRangeList,
    ISheetValue,
    ISurfaceContext,
    NamedArguments,
    SheetType,
    SheetValueType,
    SimpleNamingContext
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';

export class CurrentFunctionParam
{
    constructor(public readonly ignoreEvaluation:boolean)
    {
    }
}

const FRAME_CALC_COLUMN = AError.fromString(`@FRAME_CALC_COLUMN`);

export class EvalFormula implements IFormulaVisitorAsync
{
    private readonly _stack:EvalStack;
    private readonly _sheetIO:ISheetIOContext;
    private readonly _cellIO:ICellIOContext;
    private readonly _functionResolver:IFunctionResolver;
    private readonly _surfaceContext:ISurfaceContext;
    private readonly _context:SheetAPIImpl;
    private readonly _namedArguments:NamedArguments;
    private readonly _javaEvalContext:any;

    public constructor(stack:EvalStack,
                       sheetIO:ISheetIOContext,
                       cellIO:ICellIOContext,
                       functionResolver:IFunctionResolver,
                       surfaceContext:ISurfaceContext,
                       namedArguments?:NamedArguments,
                       javaEvalContext?:any)
    {
        this._stack = stack;
        this._sheetIO = sheetIO;
        this._cellIO = cellIO;
        this._functionResolver = functionResolver;
        this._surfaceContext = surfaceContext;
        this._context = new SheetAPIImpl(
            new SheetContextImpl(sheetIO, cellIO, functionResolver, surfaceContext, stack),
            TimerContextImpl.getInstance(),
            surfaceContext
        );
        this._namedArguments = namedArguments ? namedArguments : new Map<string, IValue>();
        this._javaEvalContext = javaEvalContext;
    }

    private get _evalLogic():EvalLogic
    {
        if( this._stack.isArrayFormula() )
            return new ArrayFormulaEvalLogic(this._stack, this._sheetIO, this._cellIO, this._functionResolver, this._context, this._namedArguments, this._javaEvalContext);

        return new EvalLogic(this._stack, this._sheetIO, this._cellIO, this._functionResolver, this._context, this._namedArguments, this._javaEvalContext);
    }

    preFormulaExpr(formulaExpr:FormulaValueExpression):Promise<any>
    {
        return;
    }

    formulaExpr(formulaExpr:FormulaValueExpression):Promise<any>
    {
        const evaluationResult:IValue = this.getEvaluationResult();
        this._stack.push(evaluationResult);
        return;
    }

    public getEvaluationResult():IValue
    {
        const valuesLength = this._stack.valuesLength();

        if( valuesLength == 0 )
        {
            mk.error(area.FormulaEval, `Evaluation stack is empty`);
            return EvalErrors.Value;
        }

        if( valuesLength > 1 )
        {
            let values:Array<IValue | ISheetValue> = [];

            for( let i = 0; i < valuesLength; i++ )
                values.push(this._stack.pop());

            values.reverse();

            mk.error(
                area.FormulaEval,
                `Evaluation stack contains ${valuesLength} values => ${FormulaValueCoercionUtils.PrintValues(
                    this._context.getSheetContext(),
                    values
                )}`
            );

            return EvalErrors.Value;
        }

        const value = this._stack.pop();

        // convert range and range list into frame
        if( SheetValueType.isInstanceOf(value, SheetType.SheetRange) ||
            SheetValueType.isInstanceOf(value, SheetType.SheetRangeList) )
        {
            const rangeList = FormulaValueCoercionUtils.ConvertToSheetRangeList(
                this._context.getSheetContext(),
                value
            );
            if( SheetValueType.isInstanceOf(rangeList, SheetType.SheetRangeList) )
            {
                let result:AFrame = null;

                const namingContext = this.getNamingContext();
                const sheetRanges = (<ISheetRangeList>rangeList).ranges;

                for( let i = 0; i < sheetRanges.length; i++ )
                {
                    const sheetRange = sheetRanges[i];

                    const frame = FormulaValueCoercionUtils.ConvertToIValue(
                        this._context.getSheetContext(),
                        namingContext,
                        sheetRange
                    );
                    if( Type.isInstanceOf(frame, Type.Frame) )
                    {
                        result = result ? result.extendWith((<AFrame>frame)) as AFrame : (<AFrame>frame);
                    }
                }

                return result;
            }
        }

        return FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            this.getNamingContext(),
            value
        ) as IValue;
    }

    preLazyExpr(lazyExpr:LazyFormulaExpression):Promise<any>
    {
        this._stack.markIsLazyFormula();
        return;
    }

    lazyExpr(lazyExpr:LazyFormulaExpression):Promise<any>
    {
        // do nothing
        return;
    }

    liveExpr(liveExpr:LiveFormulaExpression):Promise<any>
    {
        this._stack.markIsLiveFormula();
        return;
    }

    preLiveExpr(liveExpr:LiveFormulaExpression):Promise<any>
    {
        // do nothing
        return;
    }

    preParenExpr(parenExpr:ParenExpr)
    {
    }

    postParenExpr(parenExpr:ParenExpr)
    {
    }

    binaryExpr(binaryOp:BinaryExpr):Promise<any>
    {
        const rhs = this._stack.pop();
        const lhs = this._stack.pop();

        const result = this._evalLogic.binary(lhs, rhs, binaryOp);
        this._stack.push(result);
        return;
    }

    private _shouldIgnoreEval(ref:CellReference | RangeReference):boolean
    {
        const cfp = this._stack.getCurrentFunctionParam();
        if( cfp && cfp.ignoreEvaluation )
        {
            this._stack.push(AString.fromString(`#NotUsedParam (${ref.toString()})`));
            mk.debug(area.FormulaEval, `NOT READING sheet for: ${ref.toString()}`);

            return true;
        }

        return false;
    }

    cellReference(cellRef:CellReference):Promise<any>
    {
        if( this._shouldIgnoreEval(cellRef) )
            return;

        if( !this._sheetIO.isSheetExists(cellRef.sheetId) )
        {
            this._stack.push(EvalErrors.SheetRef);
            return;
        }

        this._stack.push(cellRef.toSheetLocation());
        return;
    }

    rangeReference(range:RangeReference):Promise<any>
    {
        if( this._shouldIgnoreEval(range) )
            return;

        const rhs = this._stack.pop();
        const lhs = this._stack.pop();

        const result = this._rangeReference(lhs, rhs, range);
        this._stack.push(result);
        return;
    }

    @bind
    private _rangeReference(lhsValue:IValue | ISheetValue, rhsValue:IValue | ISheetValue, range:RangeReference):IValue | ISheetRange
    {
        const lRange = FormulaValueCoercionUtils.ConvertToSheetRange(
            this._context.getSheetContext(),
            lhsValue
        ) as ISheetRange;
        const rRange = FormulaValueCoercionUtils.ConvertToSheetRange(
            this._context.getSheetContext(),
            rhsValue
        ) as ISheetRange;

        if( !lRange.sheetId || !rRange.sheetId || lRange.sheetId != rRange.sheetId )
        {
            mk.debug(area.Formula, `[toValue] rangeReference: ${range.toString()} => ${lhsValue}, ${rhsValue}`);
            return EvalErrors.Ref;
        }

        const fromColIndex = lRange.from.columnIndex;
        const fromRowIndex = lRange.from.rowIndex;

        const toColIndex = rRange.to.columnIndex;
        const toRowIndex = rRange.to.rowIndex;

        const from = new SheetLocationImpl(lRange.sheetId, fromColIndex, fromRowIndex);
        const to = new SheetLocationImpl(rRange.sheetId, toColIndex, toRowIndex);

        return new SheetRangeImpl(lRange.sheetId, from, to);
    }

    intersectionReference(intersection:IntersectionReference):Promise<any>
    {
        const rhs = this._stack.pop();
        const lhs = this._stack.pop();

        const result = this._intersectionReference(lhs, rhs, intersection);
        this._stack.push(result);
        return;
    }

    private _intersectionReference(lhs:IValue | ISheetValue, rhs:IValue | ISheetValue, intersection:IntersectionReference):ISheetRange | AError
    {
        const lRange = FormulaValueCoercionUtils.ConvertToSheetRange(
            this._context.getSheetContext(),
            lhs
        ) as ISheetRange;
        const rRange = FormulaValueCoercionUtils.ConvertToSheetRange(
            this._context.getSheetContext(),
            rhs
        ) as ISheetRange;

        if( !lRange || !rRange || lRange.sheetId != rRange.sheetId )
        {
            mk.debug(area.Formula, `[toValue] intersectionReference: ${intersection.toString()} => ${lhs}, ${rhs}`);
            return EvalErrors.Ref;
        }

        const lX1 = Math.min(lRange.from.columnIndex, lRange.to.columnIndex);
        const lY1 = Math.min(lRange.from.rowIndex, lRange.to.rowIndex);
        const lX2 = Math.max(lRange.from.columnIndex, lRange.to.columnIndex);
        const lY2 = Math.max(lRange.from.rowIndex, lRange.to.rowIndex);

        const rX1 = Math.min(rRange.from.columnIndex, rRange.to.columnIndex);
        const rY1 = Math.min(rRange.from.rowIndex, rRange.to.rowIndex);
        const rX2 = Math.max(rRange.from.columnIndex, rRange.to.columnIndex);
        const rY2 = Math.max(rRange.from.rowIndex, rRange.to.rowIndex);

        const x1 = Math.max(lX1, rX1);
        const x2 = Math.min(lX2, rX2);
        const y1 = Math.max(lY1, rY1);
        const y2 = Math.min(lY2, rY2);

        if( x2 >= x1 && y2 >= y1 )
        {
            const sheetId = lRange.sheetId;
            return new SheetRangeImpl(sheetId, new SheetLocationImpl(sheetId, x1, y1), new SheetLocationImpl(sheetId, x2, y2));
        }

        return EmptySheetRange;
    }

    public async unionReference(union:UnionReference):Promise<any>
    {
        const rhs = this._stack.pop();
        const lhs = this._stack.pop();

        const result = await this._unionReference(lhs, rhs, union);
        this._stack.push(result);

        return;
    }

    private async _unionReference(lhs:IValue | ISheetValue, rhs:IValue | ISheetValue, union:UnionReference):Promise<ISheetRangeList | IValue | AError>
    {
        const namingContext = this.getNamingContext();

        const lFrame = this._getFrame(this._context, namingContext, lhs);
        const rFrame = this._getFrame(this._context, namingContext, rhs);

        if( Type.isInstanceOf(lFrame, Type.Frame) && Type.isInstanceOf(rFrame, Type.Frame) )
        {
            return (<AFrame>lFrame).extendWith((<AFrame>rFrame));
        }

        if( Type.isInstanceOf(rhs, Type.Error) && FRAME_CALC_COLUMN.isEqualTo(rhs as IValue) )
        {
            return this._processCalculatedColumn(lhs, union.right, true);
        }

        mk.debug(area.Formula, `[toValue] unionReference: ${union.toString()} => ${lhs}, ${rhs}`);
        return EvalErrors.Ref;
    }

    private _getFrame(context:ISheetAPI, namingContext:INamingContext, value:IValue | ISheetValue)
    {
        if( SheetValueType.isInstanceOf(value, SheetType.SheetRangeList) )
        {
            return FormulaValueCoercionUtils.ConvertRangeListToFrame(
                context.getSheetContext(),
                namingContext,
                <ISheetValue>value
            );
        }

        return FormulaValueCoercionUtils.ConvertToIValue(
            context.getSheetContext(),
            namingContext,
            value
        );
    }

    public async listReference(list:ListReference):Promise<any>
    {
        const rhs = this._stack.pop();
        const lhs = this._stack.pop();

        const result = await this._listReference(lhs, rhs, list);
        this._stack.push(result);

        return;
    }

    private async _listReference(lhs:IValue | ISheetValue, rhs:IValue | ISheetValue, list:ListReference):Promise<ISheetRangeList | IValue | AError>
    {
        if( this._isValidTypeForSheetRangeList(lhs) && this._isValidTypeForSheetRangeList(rhs) )
        {
            const lRange = FormulaValueCoercionUtils.ConvertToSheetRangeList(
                this._context.getSheetContext(),
                lhs
            );
            if( Type.isInstanceOf(lRange, Type.Error) )
                return lRange as AError;

            const rRange = FormulaValueCoercionUtils.ConvertToSheetRangeList(
                this._context.getSheetContext(),
                rhs
            );
            if( Type.isInstanceOf(rRange, Type.Error) )
                return rRange as AError;

            if( SheetValueType.isInstanceOf(lRange, SheetType.SheetRangeList)
                && SheetValueType.isInstanceOf(rRange, SheetType.SheetRangeList) )
            {
                return new SheetRangeListImpl(
                    [...(<ISheetRangeList>lRange).ranges, ...(<ISheetRangeList>rRange).ranges]
                );
            }

            return EvalErrors.Ref;
        }

        mk.debug(area.Formula, `[toValue] listReference: ${list.toString()} => ${lhs}, ${rhs}`);
        return EvalErrors.Ref;
    }

    private _isValidTypeForSheetRangeList(value:IValue | ISheetValue):boolean
    {
        return SheetValueType.isInstanceOf(value, SheetType.SheetLocation)
            || SheetValueType.isInstanceOf(value, SheetType.SheetRange)
            || SheetValueType.isInstanceOf(value, SheetType.SheetRangeList);
    }

    private async _processCalculatedColumn(frameOrRange:IValue | ISheetValue, frameCalcColumnExpr:FormulaExpression, append:boolean):Promise<IFrame | AError>
    {
        let frame:IValue = frameOrRange as IValue;

        // location to frame
        if( SheetValueType.isInstanceOf(frameOrRange, SheetType.SheetLocation) )
        {
            const value = FormulaValueCoercionUtils.ConvertToIValue(
                this._context.getSheetContext(),
                this.getNamingContext(),
                frameOrRange
            );
            if( Type.isInstanceOfIValue(value) )
            {
                frame = <IValue>value;
            }
        }

        // value as frame
        if( Type.isInstanceOfIValue(frame) )
        {
            return this._processCalculatedColumnForFrame(<IValue>frame, frameCalcColumnExpr, append);
        }

        // range to frame
        // list to extended frame
        const rangeList = FormulaValueCoercionUtils.ConvertToSheetRangeList(
            this._context.getSheetContext(),
            frameOrRange
        );
        if( SheetValueType.isInstanceOf(rangeList, SheetType.SheetRangeList) )
        {
            const result:AFrame | AError = FormulaValueCoercionUtils.ConvertRangeListToFrame(
                this._context.getSheetContext(),
                this.getNamingContext(),
                <ISheetValue>rangeList
            );
            return this._processCalculatedColumnForFrame(<IValue>result, frameCalcColumnExpr, append);
        }

        return EvalErrors.Error;
    }

    comparisonExpr(comparison:ComparisonExpr):Promise<any>
    {
        const rhs = this._stack.pop();
        const lhs = this._stack.pop();

        const result = this._evalLogic.comparison(lhs, rhs, comparison);
        this._stack.push(result);
        return;
    }

    concatExpr(concatOp:ConcatExpr):Promise<any>
    {
        const rhs = this._stack.pop();
        const lhs = this._stack.pop();

        const result = this._evalLogic.concat(lhs, rhs, concatOp);
        this._stack.push(result);
        return;
    }

    public async preInvokeFunction(invokeFuncExpr:InvokeFunctionExpr):Promise<ShouldContinueTraversal>
    {
        const functionFullName:string = invokeFuncExpr.functionFullName.value;
        const fnNameLiteral:FunctionNameLiteral = FunctionUtil.parseFunctionName(functionFullName);

        const cf = await this._functionResolver.findFunctionByName(fnNameLiteral.namespace, fnNameLiteral.name);

        if( !cf )
        {
            mk.error(area.FormulaEval, `Cannot find client function for ${invokeFuncExpr.functionFullName}`);
            this._stack.push(EvalErrors.FunctionName);
            return false;
        }

        if( cf.isModifiesSpreadsheet && (!this._stack.isLazyFormula() || !this._stack.isLiveFormula()) )
        {
            this._stack.push(EvalErrors.NeedConversionToLazy);
            return false;
        }

        this._stack.pushFunction(cf);
        return true;
    }

    public preInvokeFunctionParameter(invokeFuncExpr:InvokeFunctionExpr, param:IFormulaNode, paramIndex:number)
    {
    }

    public async invokeFunctionParameter(invokeFuncExpr:InvokeFunctionExpr, param:IFormulaNode, paramIndex:number):Promise<any>
    {
        const fnNameLiteral:FunctionNameLiteral = FunctionUtil.parseFunctionName(invokeFuncExpr.functionFullName.value);
        const cf:ClientFunction = await this._functionResolver.findFunctionByName(fnNameLiteral.namespace, fnNameLiteral.name);
        // todo: take care of repeatable function argument
        const args:FunctionArg[] = cf ? cf.args.filter((arg:FunctionArg) => arg.type.type !== SheetType.SheetAPI) : [];

        const argType = paramIndex < args.length
            ? args[paramIndex] && args[paramIndex].type
            : args[args.length - 1] && args[args.length - 1].repeatable && args[args.length - 1].type;

        if( argType && argType.type === SheetType.FormulaExpression )
        {
            const argValue = new FormulaExpressionImpl(
                async (namedArguments?:NamedArguments):Promise<any> =>
                {
                    if( namedArguments )
                    {
                        const mergedNamedArguments:NamedArguments = new Map(this._namedArguments.entries());

                        namedArguments.forEach(
                            (value:IValue | ISheetValue, key:string) => mergedNamedArguments.set(key, value)
                        );

                        const result:EvalFormulaResult = await FormulaEngine
                            .getInstance()
                            .evalFormula(
                                param,
                                this._sheetIO,
                                this._cellIO,
                                this._functionResolver,
                                this._surfaceContext,
                                mergedNamedArguments
                            );

                        return result.value;
                    }
                    else
                    {
                        this.preInvokeFunctionParameter(invokeFuncExpr, param, paramIndex);
                        await param.acceptAsync(this);
                        this.postInvokeFunctionParameter(invokeFuncExpr, param, paramIndex);

                        return this._stack.pop();
                    }
                }
            );

            this._stack.push(argValue);
        }
        else
        {
            this.preInvokeFunctionParameter(invokeFuncExpr, param, paramIndex);
            await param.acceptAsync(this);
            this.postInvokeFunctionParameter(invokeFuncExpr, param, paramIndex);

            return;
        }
    }

    public postInvokeFunctionParameter(invokeFuncExpr:InvokeFunctionExpr, param:IFormulaNode, paramIndex:number)
    {
    }

    public async invokeFunction(invokeFunc:InvokeFunctionExpr):Promise<any>
    {
        let numArgs = invokeFunc.args.length;

        const argValues = [];
        for( let i = 0; i < numArgs; i++ )
            argValues.push(this._stack.pop());
        argValues.reverse();

        const result = await this._evalLogic.invokeFunction(invokeFunc, invokeFunc.args, argValues);
        this._stack.push(result);
    }

    postInvokeFunction(invokeFuncExpr:InvokeFunctionExpr):void
    {
        this._stack.popFunction();
    }

    negateExpr(negateExpr:NegateExpr):Promise<any>
    {
        const value = this._stack.pop();
        const result = this._negate(value, negateExpr);

        this._stack.push(result);
        return;
    }

    private _negate(value:IValue | ISheetValue, negateExpr:NegateExpr):IValue
    {
        const iValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            this.getNamingContext(),
            value
        ) as IValue;

        if( !iValue )
        {
            mk.debug(area.Formula, `[toValue] negateExpr: ${negateExpr.toString()} => ${value}`);
            return EvalErrors.Value;
        }

        if( iValue.isError )
        {
            mk.debug(area.Formula, `[toValue] negateExpr: ${negateExpr.toString()} => ${value}`);
            return iValue;
        }

        const aNumber:IValue = CoercionUtils.coerce(iValue, Type.Number, null);

        let result:IValue = aNumber;

        if( !aNumber.isError )
        {
            const rawValue:number = (aNumber as ANumber).value;
            result = ANumber.fromNumber(-1 * rawValue);
        }

        mk.debug(area.Formula, `[toValue] negateExpr: ${negateExpr.toString()} => ${result}`);
        return result;
    }

    parenExpr(parenExpr:ParenExpr):Promise<any>
    {
        return;
    }

    percentExpr(percentExpr:PercentExpr):Promise<any>
    {
        const value = this._stack.pop();
        const result = this._percent(value, percentExpr);

        this._stack.push(result);
        return;
    }

    private _percent(value:IValue | ISheetValue, percentExpr:PercentExpr):IValue
    {
        const iValue:IValue = FormulaValueCoercionUtils.ConvertToIValue(
            this._context.getSheetContext(),
            this.getNamingContext(),
            value
        ) as IValue;

        if( !iValue )
        {
            mk.debug(area.Formula, `[toValue] percentExpr: ${percentExpr.toString()} => ${value}`);
            return EvalErrors.Value;
        }

        if( iValue.isError )
        {
            mk.debug(area.Formula, `[toValue] percentExpr: ${percentExpr.toString()} => ${value}`);
            return iValue;
        }

        const aNumber:IValue = CoercionUtils.coerce(iValue, Type.Number, null);

        let result:IValue = aNumber;

        if( !aNumber.isError )
        {
            const rawValue:number = (aNumber as ANumber).value;
            result = ANumber.fromNumber(rawValue / 100);
        }

        mk.debug(area.Formula, `[toValue] percentExpr: ${percentExpr.toString()} => ${result}`);
        return result;
    }

    valueLiteral(value:ValueLiteral):Promise<any>
    {
        const result = value.value;
        mk.debug(area.Formula, `[toValue] valueLiteral: ${value.toString()} => ${result}`);

        this._stack.push(result);
        return;
    }

    identifierLiteral(identifier:IdentifierLiteral):Promise<any>
    {
        const key = identifier.identifier.value;

        const value = this._namedArguments.get(key) || this._namedArguments.get(key.toUpperCase());
        const result = value ? value : AError.fromString(`#${identifier}?`);

        mk.debug(area.Formula, `[toValue] identifierLiteral: ${identifier.toString()} => ${result}`);

        this._stack.push(result);
        return;
    }

    arrayExpr(array:ArrayExpr):Promise<any>
    {
        mk.debug(area.Formula, `[toValue] arrayExpr: ${array.toString()}`);

        const rows = [];

        const rowsCount = array.rows.length;
        for( let i = rowsCount - 1; i >= 0; i-- )
        {
            const cells = [];

            const cellsCount = array.rows[i].length;
            for( let j = cellsCount - 1; j >= 0; j-- )
            {
                cells[j] = this._stack.pop();
            }

            rows[i] = cells;
        }

        const frame = FormulaValueCoercionUtils.CreateFrameFromRows(
            this._context.getSheetContext(),
            this.getNamingContext(),
            rows
        );
        this._stack.push(frame);

        return;
    }

    frameColumnReference(frameColumn:FrameColumnReference):Promise<any>
    {
        const key = frameColumn.toString();

        const value = this._namedArguments.get(key);
        const result = value ? value : AError.fromString(`#${frameColumn}?`);

        mk.debug(area.Formula, `[toValue] frameColumnReference: ${frameColumn.toString()} => ${result}`);

        this._stack.push(result);
        return;
    }

    frameCalculatedColumnExpr(frameCalculatedColumn:FrameCalculatedColumnExpr):Promise<any>
    {
        this._stack.push(FRAME_CALC_COLUMN);
        return;
    }

    private getNamingContext():INamingContext
    {
        return new SimpleNamingContext();
    }

    private _processCalculatedColumnForFrame(frame:IValue, frameCalcColumnExpr:FormulaExpression, append:boolean)
    {
        if( Type.isInstanceOf(frame, Type.Frame) )
        {
            const calculatedColumnExprVisitor = new FrameCalculatedColumnExprVisitor(
                (<AFrame>frame),
                append,
                this._namedArguments,
                this._sheetIO,
                this._cellIO,
                this._functionResolver,
                this._surfaceContext
            );
            frameCalcColumnExpr.accept(calculatedColumnExprVisitor);
            return calculatedColumnExprVisitor.getResult();
        }

        return EvalErrors.Error;
    }
}

class FrameCalculatedColumnExprVisitor extends BaseFormulaVisitor
{
    private _result:Promise<IFrame | AError>;
    private _accepted:boolean;

    private readonly _frame:AFrame;
    private readonly _append:boolean;

    private readonly _namedArguments:NamedArguments;
    private readonly _sheetIO:ISheetIOContext;
    private readonly _cellIO:ICellIOContext;
    private readonly _functionResolver:IFunctionResolver;
    private readonly _surfaceContext:ISurfaceContext;

    constructor(frame:AFrame,
                append:boolean,
                namedArguments:NamedArguments,
                sheetIO:ISheetIOContext,
                cellIO:ICellIOContext,
                functionResolver:IFunctionResolver,
                surfaceContext:ISurfaceContext)
    {
        super();

        this._frame = frame;
        this._append = append;

        this._namedArguments = namedArguments;
        this._sheetIO = sheetIO;
        this._cellIO = cellIO;
        this._functionResolver = functionResolver;
        this._surfaceContext = surfaceContext;

        this._result = Promise.resolve(EvalErrors.Value);
        this._accepted = false;
    }

    public getResult():Promise<IFrame | AError>
    {
        return this._result;
    }

    public frameCalculatedColumnExpr(frameCalculatedColumn:FrameCalculatedColumnExpr)
    {
        // accept only first FrameCalculatedColumnExpr expression
        if( this._accepted )
            return;

        this._accepted = true;

        this._result = this._addCalculatedColumn(this._frame, frameCalculatedColumn, this._append);
    }

    private async _addCalculatedColumn(frame:AFrame, calculatedColumnExpr:FrameCalculatedColumnExpr, append:boolean):Promise<IFrame | AError>
    {
        const data = frame.getData();
        const fields = frame.getFields();
        const meta = frame.getMeta();

        const calcColumnName = calculatedColumnExpr.frameColumn.frameColumnName.value;

        const changeIndex = fields.findIndex(field => field.name === calcColumnName);
        const insertIndex = changeIndex >= 0
            ? -1
            : (append ? fields.length : 0);

        const namedArguments = new Map(this._namedArguments.entries());
        const rowArgName = !frame.findField('row') ? '@row' : '@_row';

        let calcColumnType = null;
        for( let rowIndex = 0; rowIndex < data.length; rowIndex++ )
        {
            const dataRow = data[rowIndex];

            // populate namedArguments
            fields.forEach((field, index) => namedArguments.set(`@${field.name}`, dataRow[index]));
            namedArguments.set(rowArgName, AInteger.fromNumber(rowIndex + 1));

            const evalResult = await FormulaEngine.getInstance().evalFormulaWithContext(
                calculatedColumnExpr.expression,
                new EvalStack(),
                this._sheetIO,
                this._cellIO,
                this._functionResolver,
                this._surfaceContext,
                namedArguments
            );

            const calcColumnValue = evalResult.value;
            if( calcColumnType == null && calcColumnValue )
                calcColumnType = calcColumnValue.getType();

            if( insertIndex >= 0 )
                dataRow.splice(insertIndex, 0, calcColumnValue);
            else
                dataRow[changeIndex] = calcColumnValue;
        }

        if( insertIndex >= 0 )
            fields.splice(insertIndex, 0, new FrameField(calcColumnName, calcColumnType || Type.String));

        return Frames.fromFieldsAndData(fields, data, meta);
    }
}
